export const SHELL_TOP_BAR_PX = 64;
export const SHELL_FOOTER_PX = 64;
export const SHELL_SIDEBAR_WIDTH_PX = 240;
export const SHELL_NAV_ROW_HEIGHT_PX = 48;

export const VIEWPORT_2K_MIN_WIDTH_PX = 2200;

export const VIEWPORT_2K_MIN_MEDIA = `@media (min-width: ${VIEWPORT_2K_MIN_WIDTH_PX}px)`;

export const VIEWPORT_2K_MIN_WIDTH_MATCH = `(min-width: ${VIEWPORT_2K_MIN_WIDTH_PX}px)`;
export const SHELL_SIDEBAR_WIDTH_2K_PX = 268;
export const SHELL_NAV_ROW_HEIGHT_2K_PX = 52;

export const VIEWPORT_4K_MIN_WIDTH_PX = 3600;
export const VIEWPORT_4K_MIN_MEDIA = `@media (min-width: ${VIEWPORT_4K_MIN_WIDTH_PX}px)`;

export const VIEWPORT_4K_MIN_WIDTH_MATCH = `(min-width: ${VIEWPORT_4K_MIN_WIDTH_PX}px)`;
export const SHELL_SIDEBAR_WIDTH_4K_PX = 292;
export const SHELL_NAV_ROW_HEIGHT_4K_PX = 56;
export const SHELL_NAV_GAP_PX = 8;
export const SHELL_NAV_ROW_INNER_SPACING = 1.5;

export const SHELL_MAIN_CHROME_PADDING_PX = 24;

export const TABLE_PAGE_VIEWPORT_SUBTRACT_PX =
  SHELL_TOP_BAR_PX + SHELL_FOOTER_PX;
export const TABLE_PAGE_CONTAINER_HEIGHT = `calc(100vh - ${TABLE_PAGE_VIEWPORT_SUBTRACT_PX}px)`;

/**
 * Compact-laptop tier. MUI's own breakpoints step lg(1200) straight to
 * xl(1536), so 1366x768 and 1920x1080 resolve to the same layout today while
 * the second has 550px more room. This is the missing rung between them, in
 * the same shape as the 2K/4K tiers above. 1440 is inclusive so a MacBook Air
 * and a windowed 1400 both get the compact treatment - above it the dense
 * tables fit as designed.
 */
export const VIEWPORT_COMPACT_MAX_WIDTH_PX = 1440;
export const VIEWPORT_COMPACT_MAX_MEDIA = `@media (max-width: ${VIEWPORT_COMPACT_MAX_WIDTH_PX}px)`;
export const VIEWPORT_COMPACT_MAX_WIDTH_MATCH = `(max-width: ${VIEWPORT_COMPACT_MAX_WIDTH_PX}px)`;

/**
 * Short-viewport tier. A 768-tall screen leaves ~625 CSS px once the browser
 * chrome and the Windows taskbar take their cut; a 900-tall one ~760. 860
 * catches both and spares a 1080-tall screen (~940).
 */
export const VIEWPORT_SHORT_MAX_HEIGHT_PX = 860;
export const VIEWPORT_SHORT_MAX_MEDIA = `@media (max-height: ${VIEWPORT_SHORT_MAX_HEIGHT_PX}px)`;
export const VIEWPORT_SHORT_MAX_HEIGHT_MATCH = `(max-height: ${VIEWPORT_SHORT_MAX_HEIGHT_PX}px)`;

/** Collapsed main navigation: icons only, labels move into tooltips. */
export const SHELL_SIDEBAR_RAIL_WIDTH_PX = 64;

/**
 * Height of the outer app shell. Mobile browsers count the collapsing URL bar
 * in 100vh, which cuts off the footer, so prefer 100dvh where it exists. One
 * source for every shell - the Client View one shipped without this guard.
 */
export const SHELL_VIEWPORT_HEIGHT_SX = {
  height: "100vh",
  maxHeight: "100vh",
  "@supports (height: 100dvh)": {
    height: "100dvh",
    maxHeight: "100dvh",
  },
} as const;

/**
 * A box with a fixed maximum that must not out-grow the real viewport.
 * `reservedPx` is what the surrounding chrome already spends, so the box keeps
 * its designed height on a tall screen and shrinks on a laptop instead of
 * pushing the controls under it off-screen. Falls back to the flat maximum
 * where dvh is unsupported, same guard as SHELL_VIEWPORT_HEIGHT_SX.
 */
export const viewportCappedHeightSx = (
  minPx: number,
  maxPx: number,
  reservedPx: number,
) =>
  ({
    height: maxPx,
    "@supports (height: 100dvh)": {
      height: `clamp(${minPx}px, calc(100dvh - ${reservedPx}px), ${maxPx}px)`,
    },
  }) as const;

/**
 * A phone on its side: ~390px of height for the whole page. Width-based rules
 * cannot see this case - 844x390 is wider than many tablets are.
 */
export const PHONE_LANDSCAPE_MATCH =
  "(max-height: 480px) and (orientation: landscape)";
export const PHONE_LANDSCAPE_MEDIA = `@media ${PHONE_LANDSCAPE_MATCH}`;
